import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import {
    Spin,
    Card,
    Input,
    Button,
    Tag,
    Avatar,
    Space,
    Drawer,
    Tooltip,
    Segmented,
    Empty,
    Select,
    Row,
    Col,
} from "antd";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import Tree from "react-d3-tree";
import {
    SearchOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    ReloadOutlined,
    UserOutlined,
    ApartmentOutlined,
    CrownFilled,
    FullscreenOutlined,
    FullscreenExitOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    BranchesOutlined,
    CompressOutlined,
    TeamOutlined,
    CheckCircleFilled,
} from "@ant-design/icons";
import { Building2, Layers, ChevronRight, Users } from "lucide-react";
import "./hod/TeamMembers.css";

// Helper to get initials
const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
        return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    }
    return (name[0] || "?").toUpperCase();
};

// Premium mesh gradients
const placeholderGradients = [
    "radial-gradient(at 0% 0%, #0e497b 0, transparent 50%), radial-gradient(at 50% 0%, #1677ff 0, transparent 50%), radial-gradient(at 100% 0%, #002c5c 0, transparent 50%)",
    "radial-gradient(at 0% 0%, #1e3a8a 0, transparent 50%), radial-gradient(at 50% 0%, #3b82f6 0, transparent 50%), radial-gradient(at 100% 0%, #172554 0, transparent 50%)",
    "radial-gradient(at 0% 0%, #1e1b4b 0, transparent 50%), radial-gradient(at 50% 0%, #4338ca 0, transparent 50%), radial-gradient(at 100% 0%, #312e81 0, transparent 50%)",
];

const getGradient = (name = "") => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return placeholderGradients[Math.abs(hash) % placeholderGradients.length];
};

export default function OrgChart({ hodId, rootId }) {
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDept, setSelectedDept] = useState("ALL");
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [viewMode, setViewMode] = useState("tree"); // "tree", "grid", "list"
    const [zoomLevel, setZoomLevel] = useState(0.85);
    const [translate, setTranslate] = useState({ x: 550, y: 70 });
    const [isFullscreen, setIsFullscreen] = useState(false);

    const { authData, backUrl } = useContext(AuthContext);
    const containerRef = useRef(null);

    const targetId = hodId || rootId || null;

    const fetchOrgTree = async () => {
        setLoading(true);
        try {
            const params = {};
            if (targetId) {
                params.root_id = targetId;
            }
            const res = await axios.get(`${backUrl}/api/emp/employees/org-chart/`, {
                params,
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });
            setTreeData(res.data || []);
        } catch (err) {
            console.error("Failed to load org chart:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrgTree();
    }, [targetId, backUrl, authData?.access]);

    // Center tree on container resize or data load
    const centerTree = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setTranslate({ x: rect.width / 2 || 550, y: 70 });
        }
        setZoomLevel(0.85);
    };

    useEffect(() => {
        centerTree();
    }, [treeData]);

    const handleZoomIn = () => {
        setZoomLevel((z) => Math.min(z + 0.15, 1.8));
    };

    const handleZoomOut = () => {
        setZoomLevel((z) => Math.max(z - 0.15, 0.35));
    };

    // Extract unique departments & statistics
    const { departmentsList, totalTreeMembers, hodNodeName } = useMemo(() => {
        const set = new Set();
        let count = 0;
        let rootHodName = "";

        const collectDepts = (nodes, isRoot = false) => {
            if (!nodes) return;
            nodes.forEach((n) => {
                count++;
                if (isRoot && n.name) rootHodName = n.name;
                if (n.department) set.add(n.department);
                if (n.children) collectDepts(n.children, false);
            });
        };
        collectDepts(treeData, true);

        return {
            departmentsList: Array.from(set),
            totalTreeMembers: count,
            hodNodeName: rootHodName,
        };
    }, [treeData]);

    // Convert nodes format for react-d3-tree
    const convertNode = (node) => {
        if (!node) return null;
        if (node.is_active === false) return null;
        const status = (node.status || "").toUpperCase();
        if (["INACTIVE", "RELIEVED", "RESIGNED", "TERMINATED"].includes(status)) return null;

        const hasChildren = Array.isArray(node.children) && node.children.length > 0;
        return {
            id: node.id,
            name: node.name || "Employee",
            emp_code: node.emp_code || "",
            title: node.title || "Team Member",
            department: node.department || "General",
            organization: node.organization || "",
            is_hod: !!node.is_hod,
            is_management: !!node.is_management,
            photo: node.photo,
            raw: node,
            children: hasChildren ? node.children.map(convertNode).filter(Boolean) : [],
        };
    };

    const formattedTree = useMemo(() => {
        if (!treeData || treeData.length === 0) return null;
        if (treeData.length === 1) {
            return convertNode(treeData[0]);
        }
        return {
            name: "Organization Hierarchy",
            title: "Executive Leadership",
            department: "Corporate",
            children: treeData.map(convertNode).filter(Boolean),
        };
    }, [treeData]);

    // Filter nodes by search & department
    const filterTree = (node, query, dept) => {
        if (!node) return null;
        const q = query.toLowerCase();

        const matchesQuery =
            !q ||
            node.name.toLowerCase().includes(q) ||
            (node.emp_code && node.emp_code.toLowerCase().includes(q)) ||
            (node.title && node.title.toLowerCase().includes(q)) ||
            (node.department && node.department.toLowerCase().includes(q));

        const matchesDept = dept === "ALL" || node.department === dept;
        const selfMatch = matchesQuery && matchesDept;

        let filteredChildren = [];
        if (node.children && node.children.length > 0) {
            filteredChildren = node.children
                .map((c) => filterTree(c, query, dept))
                .filter(Boolean);
        }

        if (selfMatch || filteredChildren.length > 0) {
            return {
                ...node,
                children: filteredChildren,
            };
        }
        return null;
    };

    const displayTree = useMemo(() => {
        if (!formattedTree) return null;
        if (!searchTerm.trim() && selectedDept === "ALL") return formattedTree;
        return filterTree(formattedTree, searchTerm.trim(), selectedDept);
    }, [formattedTree, searchTerm, selectedDept]);

    const flattenedEmployees = useMemo(() => {
        const list = [];
        const flattenRec = (nodes) => {
            if (!nodes) return;
            nodes.forEach((n) => {
                list.push(n);
                if (n.children) flattenRec(n.children);
            });
        };
        if (displayTree) flattenRec([displayTree]);
        return list;
    }, [displayTree]);

    // Exact Team Members Hierarchy Model (TL Groups + Direct Reports)
    const hierarchyGroups = useMemo(() => {
        if (!displayTree) return { tlGroups: [], directReports: [] };

        const tlGroups = [];
        const directReports = [];

        const traverse = (node) => {
            if (!node) return;
            const children = node.children || [];

            if (children.length > 0) {
                // Node has subordinates -> It is a TL / Manager
                tlGroups.push({
                    tl: node,
                    members: children,
                });
                children.forEach((c) => traverse(c));
            }
        };

        const roots = Array.isArray(displayTree) ? displayTree : [displayTree];
        roots.forEach((root) => {
            const rootChildren = root.children || [];
            rootChildren.forEach((child) => {
                if (child.children && child.children.length > 0) {
                    traverse(child);
                } else {
                    directReports.push(child);
                }
            });
        });

        return { tlGroups, directReports };
    }, [displayTree]);

    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch((err) => console.error(err));
            setIsFullscreen(true);
        } else {
            document.exitFullscreen().catch((err) => console.error(err));
            setIsFullscreen(false);
        }
    };

    const handleNodeClick = (nodeDatum) => {
        if (nodeDatum.raw || nodeDatum) {
            setSelectedEmployee(nodeDatum.raw || nodeDatum);
            setDrawerVisible(true);
        }
    };

    // Modern Matrimony / Team Members Style Employee Grid Card
    const ModernGridCard = ({ emp, isTL = false }) => {
        const isHod = emp.is_hod;
        const isMgmt = emp.is_management;

        return (
            <div
                className={`glass-card ${isTL || isHod ? "tl-card" : ""}`}
                onClick={() => handleNodeClick(emp)}
                style={{ borderRadius: 20, cursor: "pointer", height: "100%" }}
            >
                <div className="photo-container">
                    {emp.photo ? (
                        <img src={emp.photo} alt={emp.name} className="profile-photo" />
                    ) : (
                        <div
                            className="profile-photo"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: getGradient(emp.name),
                                color: "white",
                                position: "relative",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: "44px",
                                    fontWeight: 900,
                                    letterSpacing: "3px",
                                    textShadow: "0 6px 14px rgba(0,0,0,0.3)",
                                    zIndex: 1,
                                    fontFamily: "Inter, sans-serif",
                                }}
                            >
                                {getInitials(emp.name)}
                            </div>
                        </div>
                    )}

                    <div
                        className="status-badge-floating"
                        style={{
                            background: isHod
                                ? "rgba(14, 73, 123, 0.95)"
                                : isTL
                                ? "rgba(217, 119, 6, 0.95)"
                                : "rgba(37, 99, 235, 0.95)",
                            color: "#ffffff",
                            border: "none",
                            fontWeight: 900,
                        }}
                    >
                        {isHod ? "★ HOD" : isTL ? "TEAM LEAD" : "MEMBER"}
                    </div>

                    <div className="photo-overlay">
                        <div className="overlay-name">
                            {emp.name}
                        </div>
                    </div>
                </div>

                <div className="card-info-section">
                    <div className="designation-text" style={{ fontSize: "14px" }}>
                        {emp.title || emp.designation_name || "Team Member"}
                    </div>

                    <div className="dept-text">
                        <Building2 size={14} strokeWidth={2} />
                        <span style={{ fontWeight: 500 }}>{emp.department || "General"}</span>
                    </div>

                    <div className="dept-text" style={{ marginTop: 4 }}>
                        <Layers size={14} />
                        <span style={{ color: "#94a3b8" }}>{emp.emp_code || "—"}</span>
                    </div>
                </div>

                <div className="card-action-bar">
                    <button className="view-profile-btn">
                        View Details
                        <ChevronRight size={14} />
                    </button>
                </div>
            </div>
        );
    };

    // High-Contrast Executive Node Card Element for Tree Canvas
    const renderNode = ({ nodeDatum, toggleNode }) => {
        const isHod = nodeDatum.is_hod;
        const isMgmt = nodeDatum.is_management;
        const directReportsCount = nodeDatum.children ? nodeDatum.children.length : 0;
        const isCollapsed = nodeDatum.__rd3t.collapsed;

        return (
            <g>
                <foreignObject width={275} height={126} x={-137} y={-52}>
                    <div
                        onClick={() => handleNodeClick(nodeDatum)}
                        style={{
                            width: "100%",
                            height: "100%",
                            background: "#ffffff",
                            borderRadius: 14,
                            border: isHod
                                ? "2.5px solid #0e497b"
                                : isMgmt
                                ? "2px solid #d97706"
                                : "1.5px solid #cbd5e1",
                            boxShadow: isHod
                                ? "0 8px 22px rgba(14, 73, 123, 0.16)"
                                : "0 4px 14px rgba(15, 23, 42, 0.08)",
                            boxSizing: "border-box",
                            cursor: "pointer",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            transition: "all 0.2s ease",
                        }}
                    >
                        {/* Executive Top Header Bar */}
                        <div
                            style={{
                                background: isHod
                                    ? "linear-gradient(135deg, #0e497b 0%, #1f6db8 100%)"
                                    : isMgmt
                                    ? "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)"
                                    : "#f8fafc",
                                padding: "5px 12px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                borderBottom: "1px solid #e2e8f0",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: isHod || isMgmt ? "#ffffff" : "#475569",
                                    letterSpacing: "0.6px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                }}
                            >
                                {isHod ? <CrownFilled style={{ color: "#fbbf24" }} /> : null}
                                {isHod ? "★ HOD LEAD" : isMgmt ? "MANAGEMENT" : "TEAM MEMBER"}
                            </span>

                            {nodeDatum.emp_code && (
                                <span
                                    style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: isHod || isMgmt ? "#ffffff" : "#334155",
                                        background: isHod || isMgmt ? "rgba(255, 255, 255, 0.25)" : "#e2e8f0",
                                        padding: "1px 6px",
                                        borderRadius: 4,
                                    }}
                                >
                                    {nodeDatum.emp_code}
                                </span>
                            )}
                        </div>

                        {/* Card Content Body */}
                        <div style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                            <div style={{ position: "relative", flexShrink: 0 }}>
                                <Avatar
                                    src={nodeDatum.photo}
                                    icon={!nodeDatum.photo && <UserOutlined />}
                                    size={44}
                                    style={{
                                        border: isHod ? "2px solid #0e497b" : "1.5px solid #cbd5e1",
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                        backgroundColor: isHod ? "#0e497b" : "#64748b",
                                    }}
                                />
                                <CheckCircleFilled
                                    style={{
                                        position: "absolute",
                                        bottom: -2,
                                        right: -2,
                                        color: "#10b981",
                                        fontSize: 12,
                                        background: "#ffffff",
                                        borderRadius: "50%",
                                    }}
                                />
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 800,
                                        color: "#0f172a",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        lineHeight: "1.3",
                                    }}
                                    title={nodeDatum.name}
                                >
                                    {nodeDatum.name}
                                </div>
                                <div
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: "#0e497b",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        lineHeight: "1.3",
                                        marginTop: 1,
                                    }}
                                    title={nodeDatum.title || "Team Member"}
                                >
                                    {nodeDatum.title || "Team Member"}
                                </div>
                                <div
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: "#0e497b",
                                        background: "#e6f0fa",
                                        padding: "1px 7px",
                                        borderRadius: 10,
                                        marginTop: 4,
                                        border: "1px solid #b9d7f2",
                                    }}
                                >
                                    <ApartmentOutlined style={{ marginRight: 4 }} />
                                    {nodeDatum.department || "General"}
                                </div>
                            </div>
                        </div>

                        {/* Footer Subordinates Toggle Button */}
                        {directReportsCount > 0 && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleNode();
                                }}
                                style={{
                                    padding: "4px 12px",
                                    background: isHod ? "#f0f7ff" : "#f8fafc",
                                    borderTop: "1px solid #e2e8f0",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "#0e497b",
                                    cursor: "pointer",
                                }}
                            >
                                <span>
                                    <TeamOutlined style={{ marginRight: 5 }} />
                                    {directReportsCount} {directReportsCount === 1 ? "Direct Report" : "Direct Reports"}
                                </span>
                                <span
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 900,
                                        background: "#0e497b",
                                        color: "#ffffff",
                                        width: 18,
                                        height: 18,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {isCollapsed ? "+" : "−"}
                                </span>
                            </div>
                        )}
                    </div>
                </foreignObject>
            </g>
        );
    };

    return (
        <Card
            bordered={false}
            style={{
                borderRadius: 16,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
            }}
            bodyStyle={{ padding: 20 }}
        >
            {/* Header Control Panel */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 20,
                    flexWrap: "wrap",
                    gap: 16,
                    background: "#ffffff",
                    padding: 14,
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.02)",
                }}
            >
                {/* Search & Department Selector Dropdown */}
                <Space size="middle" wrap>
                    <Input
                        prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                        placeholder="Search employee, designation..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                        style={{ width: 260, borderRadius: 8 }}
                    />

                    {departmentsList.length > 0 && (
                        <Select
                            value={selectedDept}
                            onChange={(val) => setSelectedDept(val)}
                            style={{ width: 220 }}
                            placeholder="Filter Department"
                        >
                            <Select.Option value="ALL">All Departments ({departmentsList.length})</Select.Option>
                            {departmentsList.map((d) => (
                                <Select.Option key={d} value={d}>
                                    {d}
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                </Space>

                {/* View Switcher & Action Buttons */}
                <Space size="small" wrap>
                    <Segmented
                        options={[
                            { label: "Flow Chart Tree", value: "tree", icon: <BranchesOutlined /> },
                            { label: "Grid View", value: "grid", icon: <AppstoreOutlined /> },
                            { label: "List View", value: "list", icon: <UnorderedListOutlined /> },
                        ]}
                        value={viewMode}
                        onChange={(v) => setViewMode(v)}
                        style={{ background: "#f1f5f9" }}
                    />

                    {viewMode === "tree" && (
                        <>
                            <Tooltip title="Zoom In (+)">
                                <Button
                                    icon={<ZoomInOutlined />}
                                    onClick={handleZoomIn}
                                />
                            </Tooltip>
                            <Tooltip title="Zoom Out (-)">
                                <Button
                                    icon={<ZoomOutOutlined />}
                                    onClick={handleZoomOut}
                                />
                            </Tooltip>
                            <Tooltip title="Reset View">
                                <Button
                                    icon={<CompressOutlined />}
                                    onClick={centerTree}
                                />
                            </Tooltip>
                        </>
                    )}

                    <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                        <Button
                            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={toggleFullscreen}
                        />
                    </Tooltip>
                    <Tooltip title="Reload Tree">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchOrgTree}
                        />
                    </Tooltip>
                </Space>
            </div>

            {/* Tree Canvas Container */}
            <div
                ref={containerRef}
                style={{
                    width: "100%",
                    height: 700,
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {loading ? (
                    <div style={{ display: "flex", height: "100%", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 12 }}>
                        <Spin size="large" />
                        <span style={{ color: "#64748b", fontSize: 14 }}>Loading Interactive Flow Chart Tree...</span>
                    </div>
                ) : displayTree ? (
                    viewMode === "tree" ? (
                        <Tree
                            data={displayTree}
                            translate={translate}
                            zoom={zoomLevel}
                            orientation="vertical"
                            collapsible={true}
                            zoomable={true}
                            draggable={true}
                            scaleExtent={{ min: 0.25, max: 2.2 }}
                            pathFunc="step"
                            transitionDuration={450}
                            nodeSize={{ x: 295, y: 175 }}
                            separation={{ siblings: 1.25, nonSiblings: 1.6 }}
                            renderCustomNodeElement={renderNode}
                        />
                    ) : viewMode === "grid" ? (
                        <div style={{ width: "100%", height: "100%", padding: 24, overflowY: "auto" }}>
                            {/* TL Teams Grouping (Matching TeamMembers.jsx lines 956-1000) */}
                            {hierarchyGroups.tlGroups.map(({ tl, members }) => (
                                <div key={tl.id} style={{ marginBottom: 40 }}>
                                    <div className="team-group-header" style={{ marginBottom: 16 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <Avatar src={tl.photo} icon={<UserOutlined />} style={{ border: "2px solid #0e497b" }} />
                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                <span style={{ fontWeight: 700, color: "#0e497b", fontSize: 16 }}>
                                                    {tl.name}
                                                </span>
                                                <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                                                    {tl.department || "Department"}
                                                </span>
                                            </div>
                                            <span className="tl-badge">TEAM LEAD</span>
                                        </div>
                                        <div style={{ fontSize: 12, color: "#64748b" }}>
                                            {members.length} Members
                                        </div>
                                    </div>

                                    <Row gutter={[20, 20]}>
                                        {/* Show the TL card first */}
                                        <Col xs={24} sm={12} md={8} lg={6}>
                                            <ModernGridCard emp={tl} isTL={true} />
                                        </Col>
                                        {/* Show their team members */}
                                        {members.map((member) => (
                                            <Col key={member.id} xs={24} sm={12} md={8} lg={6}>
                                                <ModernGridCard emp={member} isTL={false} />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))}

                            {/* Direct Reports Section (Matching TeamMembers.jsx lines 1002-1022) */}
                            {hierarchyGroups.directReports.length > 0 && (
                                <div style={{ marginBottom: 40 }}>
                                    <div className="team-group-header" style={{ marginBottom: 16 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                            <Users size={20} color="#0e497b" />
                                            <span style={{ fontWeight: 700, color: "#0e497b", fontSize: 16 }}>Direct Reports</span>
                                        </div>
                                    </div>
                                    <Row gutter={[20, 20]}>
                                        {hierarchyGroups.directReports.map((dr) => (
                                            <Col key={dr.id} xs={24} sm={12} md={8} lg={6}>
                                                <ModernGridCard emp={dr} isTL={dr.is_hod} />
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ width: "100%", height: "100%", padding: 20, overflowY: "auto" }}>
                            {flattenedEmployees.map((emp) => (
                                <Card
                                    key={emp.id}
                                    size="small"
                                    style={{ marginBottom: 10, borderRadius: 8, borderColor: "#e2e8f0" }}
                                    onClick={() => handleNodeClick(emp)}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                                        <Avatar src={emp.photo} icon={!emp.photo && <UserOutlined />} size={40} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 700, color: "#0f172a" }}>{emp.name} {emp.emp_code ? `(${emp.emp_code})` : ""}</div>
                                            <div style={{ fontSize: 12, color: "#64748b" }}>{emp.title} • {emp.department}</div>
                                        </div>
                                        {emp.is_hod && <Tag color="blue" style={{ background: "#e6f0fa", color: "#0e497b", borderColor: "#90c2ec" }}>HOD</Tag>}
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )
                ) : (
                    <div style={{ display: "flex", height: "100%", justifyContent: "center", alignItems: "center", color: "#64748b" }}>
                        <Empty description={<span style={{ color: "#64748b" }}>No structure found matching criteria</span>} />
                    </div>
                )}
            </div>

            {/* Profile Drawer */}
            <Drawer
                title={
                    <Space align="center">
                        <Avatar
                            src={selectedEmployee?.photo}
                            icon={!selectedEmployee?.photo && <UserOutlined />}
                            style={{ backgroundColor: "#0e497b" }}
                            size={44}
                        />
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
                                {selectedEmployee?.name}
                            </div>
                            <div style={{ fontSize: 12, color: "#64748b" }}>
                                {selectedEmployee?.emp_code ? `Employee Code: ${selectedEmployee.emp_code}` : ""}
                            </div>
                        </div>
                    </Space>
                }
                placement="right"
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                width={400}
            >
                {selectedEmployee && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        <Card size="small" style={{ borderRadius: 12, background: "#f8fafc", borderColor: "#e2e8f0" }}>
                            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 12 }}>Designation:</span>
                                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>
                                        {selectedEmployee.title}
                                    </div>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 12 }}>Department:</span>
                                    <div style={{ fontWeight: 700, color: "#0e497b", fontSize: 14 }}>
                                        {selectedEmployee.department}
                                    </div>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 12 }}>Organization:</span>
                                    <div style={{ fontWeight: 600, color: "#334155" }}>
                                        {selectedEmployee.organization || "Baashyaam Group"}
                                    </div>
                                </div>
                                <div>
                                    <span style={{ color: "#64748b", fontSize: 12 }}>Position Level:</span>
                                    <div style={{ marginTop: 4 }}>
                                        {selectedEmployee.is_hod ? (
                                            <Tag color="blue" icon={<CrownFilled />} style={{ background: "#e6f0fa", color: "#0e497b", borderColor: "#90c2ec" }}>Head of Department (HOD)</Tag>
                                        ) : selectedEmployee.is_management ? (
                                            <Tag color="gold">Management Executive</Tag>
                                        ) : (
                                            <Tag color="default">Team Member</Tag>
                                        )}
                                    </div>
                                </div>
                                {selectedEmployee.children && selectedEmployee.children.length > 0 && (
                                    <div>
                                        <span style={{ color: "#64748b", fontSize: 12 }}>Direct Subordinates:</span>
                                        <div style={{ fontWeight: 700, color: "#059669", fontSize: 14 }}>
                                            {selectedEmployee.children.length} Direct Reports
                                        </div>
                                    </div>
                                )}
                            </Space>
                        </Card>
                    </div>
                )}
            </Drawer>
        </Card>
    );
}
